import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// v3.7.0 Phase 3 — detecta tenant pelo subdomínio do navegador.
// Em produção: pizzariadojoao.damattsystems.com.br → slug = "pizzariadojoao"
// Em preview/local: cai no fallback "default" no backend.
// Pode ser sobrescrito via ?tenant=<slug> na URL (útil pra preview).
const ROOT_DOMAIN = 'damattsystems.com.br';
const RESERVED_SUBS = new Set(['www', 'admin', 'app', 'api', 'menu']);
const RESERVED_ROOT_PATHS = new Set([
  'admin', 'login', 'checkout', 'cardapio', 'c', 'sobre', 'termos', 'privacidade',
  'api', 'static', 'assets', 'favicon.ico', 'manifest.json', 'robots.txt',
]);

function _detectTenantSlug() {
  try {
    // 1) Override via query param
    const qp = new URLSearchParams(window.location.search).get('tenant');
    if (qp) return qp.toLowerCase().trim();
    // 2) Path /cardapio/:slug, /c/:slug ou /:slug (raiz)
    const path = window.location.pathname;
    const cardapioMatch = path.match(/^\/cardapio\/([^/]+)/);
    if (cardapioMatch) return cardapioMatch[1].toLowerCase().trim();
    const shortMatch = path.match(/^\/c\/([^/]+)/);
    if (shortMatch) return shortMatch[1].toLowerCase().trim();
    const rootMatch = path.match(/^\/([^/]+)/);
    if (rootMatch) {
      const slug = rootMatch[1].toLowerCase().trim();
      if (slug && !RESERVED_ROOT_PATHS.has(slug)) return slug;
    }
    // 3) Subdomain do hostname
    const host = (window.location.hostname || '').toLowerCase();
    if (host && host.endsWith(`.${ROOT_DOMAIN}`)) {
      const sub = host.slice(0, host.length - ROOT_DOMAIN.length - 1);
      if (sub && !RESERVED_SUBS.has(sub)) return sub;
    }
  } catch { /* ignore */ }
  return null;
}

export const TENANT_SLUG = _detectTenantSlug();

const api = axios.create({ baseURL: API_BASE });

const TOKEN_KEY = 'trace_admin_token';

export const getToken = () => {
  try { return sessionStorage.getItem(TOKEN_KEY); } catch { return null; }
};
export const setToken = (t) => {
  try {
    if (t) sessionStorage.setItem(TOKEN_KEY, t);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch { /* ignore */ }
};

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // v3.7.0 — propaga o tenant detectado para o backend via header.
  // Recalcula a cada requisição para suportar navegacao SPA e paths /cardapio/:slug.
  // Backend usa nas rotas /api/public/* (sem auth). Em rotas autenticadas,
  // o tenant_id vem do JWT, então o header é ignorado (mas não atrapalha).
  const slug = _detectTenantSlug();
  if (slug) config.headers['X-Tenant-Slug'] = slug;
  // Envia offset de fuso horario para endpoints que filtram por data
  const offset = new Date().getTimezoneOffset();
  const hours = Math.abs(Math.floor(offset / 60));
  const minutes = Math.abs(offset % 60);
  const sign = offset <= 0 ? '+' : '-';
  const tzOffset = `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  if (config.method?.toLowerCase() === 'get') {
    config.params = config.params || {};
    config.params.tz_offset = config.params.tz_offset || tzOffset;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      setToken(null);
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
    }
    // Detect the strict cashier-block 403s from the backend and dispatch a
    // global event so a friendly modal can be shown anywhere in the app.
    if (err.response?.status === 403) {
      const detail = err.response?.data?.detail || '';
      if (typeof detail === 'string' && detail.toLowerCase().includes('caixa fechado')) {
        err.cashClosed = true; // flag so callers can skip duplicate toasts
        try {
          window.dispatchEvent(new CustomEvent('cash-closed-block', { detail: { message: detail } }));
        } catch { /* ignore */ }
      }
    }
    return Promise.reject(err);
  },
);

export default api;
